import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, CheckCircle2, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/DateInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { euro, formatApiErrorDetail } from "@/lib/format";
import { todayIso, fmtDay } from "@/lib/cantieri";

const CATEGORIE = { tasse: "Tasse / F24", rata: "Rata (mutuo, leasing…)", fornitore: "Fornitore", stipendi: "Stipendi", altro: "Altro" };
const RICORRENZE = { una_volta: "Una volta", mensile: "Ogni mese", trimestrale: "Ogni 3 mesi", annuale: "Ogni anno" };
const blank = () => ({ titolo: "", categoria: "tasse", importo: "", data_scadenza: todayIso(), ricorrenza: "una_volta", fine_ricorrenza: "", note: "" });
const F = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label className="text-xs">{label}</Label>{children}</div>;

export default function PaymentDialog({ open, onClose, onSaved, initial, occ }) {
  const [f, setF] = useState(blank());
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  useEffect(() => { if (open) setF(initial ? { ...blank(), ...initial, importo: initial.importo ?? "" } : blank()); }, [open, initial]);

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = () => run(() => {
    const body = { ...f, importo: f.importo === "" ? null : Number(f.importo), fine_ricorrenza: f.ricorrenza === "una_volta" ? "" : f.fine_ricorrenza };
    return initial?.id ? api.put(`/pagamenti/${initial.id}`, body) : api.post("/pagamenti", body);
  }, initial?.id ? "Promemoria aggiornato" : "Promemoria pagamento aggiunto in agenda");
  const togglePaid = () => run(() => api.post(`/pagamenti/${initial.id}/pagato`, { data: occ.data, pagato: !occ.pagato }), occ.pagato ? "Segnato come da pagare" : "Segnato come pagato");
  const del = () => run(() => api.delete(`/pagamenti/${initial.id}`), "Promemoria eliminato");

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Promemoria pagamento" : "Nuovo promemoria pagamento"}</DialogTitle>
          <DialogDescription>Ti avvisiamo il giorno prima e il giorno stesso (alle 7:30).</DialogDescription>
        </DialogHeader>
        {occ && (
          <div className={`rounded-lg p-3 flex items-center gap-3 text-sm ${occ.pagato ? "bg-emerald-50 dark:bg-emerald-950/30" : "bg-rose-50 dark:bg-rose-950/30"}`} data-testid="payment-occurrence-box">
            <div className="flex-1">Scadenza del <b>{fmtDay(occ.data)}</b>{occ.importo ? <> · {euro(occ.importo)}</> : null} · {occ.pagato ? "pagato" : "da pagare"}</div>
            <Button size="sm" variant={occ.pagato ? "outline" : "default"} onClick={togglePaid} disabled={busy} data-testid="payment-toggle-paid">
              {occ.pagato ? <><RotateCcw className="w-4 h-4 mr-1" /> Da pagare</> : <><CheckCircle2 className="w-4 h-4 mr-1" /> Segna pagato</>}
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <F label="Descrizione *" className="col-span-2"><Input value={f.titolo} onChange={(e) => set({ titolo: e.target.value })} placeholder="Es. F24 IVA, rata furgone" data-testid="payment-title-input" /></F>
          <F label="Tipo">
            <Select value={f.categoria} onValueChange={(categoria) => set({ categoria })}>
              <SelectTrigger data-testid="payment-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CATEGORIE).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Importo (€)"><Input type="number" min="0" step="0.01" value={f.importo} onChange={(e) => set({ importo: e.target.value })} data-testid="payment-amount-input" /></F>
          <F label={f.ricorrenza === "una_volta" ? "Scadenza" : "Prima scadenza"}><DateInput value={f.data_scadenza} onChange={(e) => set({ data_scadenza: e.target.value })} data-testid="payment-date-input" /></F>
          <F label="Si ripete">
            <Select value={f.ricorrenza} onValueChange={(ricorrenza) => set({ ricorrenza })}>
              <SelectTrigger data-testid="payment-recurrence-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(RICORRENZE).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          {f.ricorrenza !== "una_volta" && <F label="Fino al (facoltativo)" className="col-span-2"><DateInput value={f.fine_ricorrenza || ""} onChange={(e) => set({ fine_ricorrenza: e.target.value })} data-testid="payment-until-input" /></F>}
          <F label="Note" className="col-span-2"><Textarea value={f.note || ""} onChange={(e) => set({ note: e.target.value })} rows={2} data-testid="payment-notes-input" /></F>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          {initial?.id ? <Button variant="outline" className="text-destructive" onClick={del} disabled={busy} data-testid="payment-delete-button"><Trash2 className="w-4 h-4 mr-1.5" /> Elimina</Button> : <span />}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Annulla</Button>
            <Button onClick={save} disabled={busy || !f.titolo || !f.data_scadenza} data-testid="payment-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
